/**
 * VIVA HUB Groups management
 */

import React, { useState, useEffect } from 'react'
import { groupsAPI } from '../../utils/scheduler/api'
import { useToast } from '../../contexts/ToastContext'
import ConfirmDialog from '../scheduler/ConfirmDialog'
import HubAdminTabs from './HubAdminTabs'
import {
  ALL_PERMISSION_OPTIONS,
  GROUP_PRESETS,
  HUB_ACCESS_PERMISSIONS,
  SCHEDULER_FEATURE_PERMISSIONS,
} from '../../constants/permission-options.js'
import { GROUP_LABELS } from '../../constants/hub-groups.js'
import { Button } from '@/components/ui/button'
import {
  DatatableCard,
  DatatableEmpty,
  DatatableScroll,
  DatatableTh,
  DATATABLE_CELL_CLASS,
  DATATABLE_CELL_PRIMARY_CLASS,
  DATATABLE_TABLE_CLASS,
  DATATABLE_TBODY_CLASS,
  DATATABLE_THEAD_CLASS,
  useClientDatatable,
} from '../shared/Datatable'
import DatatablePagination from '../shared/DatatablePagination'

const EMPTY_GROUP_FORM = {
  name: '',
  permissions: [],
  active: true,
}

function parsePermissions(permissions) {
  if (Array.isArray(permissions)) return permissions
  if (typeof permissions === 'string') {
    try {
      const parsed = JSON.parse(permissions)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function getGroupDisplayLabel(name) {
  return GROUP_LABELS[name] || name
}

function getGroupAccessItems(group) {
  const perms = parsePermissions(group.permissions)
  if (perms.length === 0) return ['No access configured']

  const labelById = Object.fromEntries(ALL_PERMISSION_OPTIONS.map((p) => [p.id, p.label]))
  return perms.map((id) => labelById[id] || id)
}

export default function HubGroupsSettings() {
  const { success: toastSuccess, error: toastError } = useToast()
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null })

  const groupsTable = useClientDatatable(groups, {
    pageLength: 10,
    initialSort: { key: 'name', dir: 'asc' },
    getSortValue: (group, key) => {
      if (key === 'name') return getGroupDisplayLabel(group.name)
      if (key === 'access') return getGroupAccessItems(group).join(', ')
      if (key === 'status') return group.active === 0 ? 0 : 1
      return group[key]
    },
  })

  const loadData = async () => {
    try {
      setLoading(true)
      const data = await groupsAPI.list()
      setGroups(data.groups || [])
    } catch (error) {
      console.error('Error loading groups:', error)
      toastError(error.message || 'Failed to load groups')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    setShowForm(false)
    setEditingItem(null)
    setFormData({})
  }, [])

  const handleEdit = (item) => {
    setEditingItem(item)
    setFormData({
      name: item.name,
      permissions: parsePermissions(item.permissions),
      active: item.active === 1,
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    try {
      await groupsAPI.delete(id)
      toastSuccess('Group deleted.')
      loadData()
    } catch (error) {
      toastError(error.message || 'Failed to delete')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (!formData.permissions || formData.permissions.length === 0) {
        toastError('At least one permission is required')
        return
      }
      if (editingItem) {
        await groupsAPI.update(editingItem.id, formData)
        toastSuccess('Group updated.')
      } else {
        await groupsAPI.create(formData)
        toastSuccess('Group created.')
      }
      setShowForm(false)
      setEditingItem(null)
      setFormData({})
      loadData()
    } catch (error) {
      toastError(error.message || 'Failed to save')
    }
  }

  const togglePermission = (permissionId, checked) => {
    const current = formData.permissions || []
    if (checked) {
      setFormData({ ...formData, permissions: [...current, permissionId] })
    } else {
      setFormData({ ...formData, permissions: current.filter((p) => p !== permissionId) })
    }
  }

  const openAddGroupForm = () => {
    setShowForm(true)
    setEditingItem(null)
    setFormData({ ...EMPTY_GROUP_FORM })
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div>
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete group?"
        message="This will permanently remove this group."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onClose={() => setDeleteConfirm({ open: false, item: null })}
        onConfirm={async () => {
          const id = deleteConfirm.item?.id
          setDeleteConfirm({ open: false, item: null })
          if (id) await handleDelete(id)
        }}
      >
        {deleteConfirm.item ? (
          <div className="font-medium text-gray-900">{deleteConfirm.item.name || 'Group'}</div>
        ) : null}
      </ConfirmDialog>

      <HubAdminTabs activeTab="groups" />

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
        <h2 className="text-xl font-semibold">Manage Groups</h2>
        {!showForm && (
          <Button type="button" variant="success" onClick={openAddGroupForm} className="w-full sm:w-auto">
            Add New Group
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">{editingItem ? 'Edit' : 'Add New'} Group</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingItem && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Use a preset (recommended)</p>
                <div className="flex flex-wrap gap-2">
                  {GROUP_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          name: preset.name,
                          permissions: [...preset.permissions],
                        })
                      }
                      className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-gray-50 hover:bg-green-50 hover:border-green-400"
                      title={preset.apps.length ? `Apps: ${preset.apps.join(', ')}` : undefined}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
              <input
                type="text"
                required
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter group name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Access permissions *</label>
              <div className="space-y-4 border border-gray-300 rounded-md p-3 max-h-80 overflow-y-auto">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">VIVA HUB app access</h4>
                  <div className="space-y-2">
                    {HUB_ACCESS_PERMISSIONS.map(({ id, label }) => (
                      <label key={id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(formData.permissions || []).includes(id)}
                          onChange={(e) => togglePermission(id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Room Scheduler features</h4>
                  <div className="space-y-2">
                    {SCHEDULER_FEATURE_PERMISSIONS.map(({ id, label }) => (
                      <label key={id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={(formData.permissions || []).includes(id)}
                          onChange={(e) => togglePermission(id, e.target.checked)}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              {(!formData.permissions || formData.permissions.length === 0) && (
                <p className="text-sm text-red-500 mt-1">At least one permission is required</p>
              )}
            </div>
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active !== false}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                type="submit"
                variant="success"
                disabled={!formData.permissions || formData.permissions.length === 0}
              >
                {editingItem ? 'Update' : 'Create'} Group
              </Button>
              <Button
                type="button"
                variant="muted"
                onClick={() => {
                  setShowForm(false)
                  setEditingItem(null)
                  setFormData({})
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <DatatableCard footer={<DatatablePagination {...groupsTable} itemLabel="groups" />}>
          <DatatableScroll>
            <table className={DATATABLE_TABLE_CLASS}>
              <thead className={DATATABLE_THEAD_CLASS}>
                <tr>
                  <DatatableTh sortKey="name" sort={groupsTable.sort} onSort={groupsTable.toggleSort}>
                    Name
                  </DatatableTh>
                  <DatatableTh sortKey="access" sort={groupsTable.sort} onSort={groupsTable.toggleSort}>
                    Access
                  </DatatableTh>
                  <DatatableTh sortKey="status" sort={groupsTable.sort} onSort={groupsTable.toggleSort}>
                    Status
                  </DatatableTh>
                  <DatatableTh excludeFromOrdering align="right">
                    Actions
                  </DatatableTh>
                </tr>
              </thead>
              <tbody className={DATATABLE_TBODY_CLASS}>
                {groupsTable.sortedItems.length === 0 ? (
                  <DatatableEmpty colSpan={4} message="No groups found" />
                ) : (
                  groupsTable.rows.map((group) => {
                    const accessItems = getGroupAccessItems(group)
                    const hasCanonicalLabel = Boolean(GROUP_LABELS[group.name])
                    return (
                      <tr key={group.id} className="hover:bg-gray-50/80">
                        <td className={DATATABLE_CELL_PRIMARY_CLASS}>
                          <div className="font-medium text-gray-900">{getGroupDisplayLabel(group.name)}</div>
                          {hasCanonicalLabel && (
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{group.name}</div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1.5 max-w-xl">
                            {accessItems.map((item) => (
                              <span
                                key={`${group.id}-${item}`}
                                className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-800"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className={DATATABLE_CELL_CLASS}>
                          {group.active === 0 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Inactive
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          )}
                        </td>
                        <td className={`${DATATABLE_CELL_CLASS} text-end font-medium`}>
                          <div className="flex items-center justify-end gap-3">
                            <button
                              type="button"
                              onClick={() => handleEdit(group)}
                              className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteConfirm({ open: true, item: group })}
                              className="inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </DatatableScroll>
        </DatatableCard>
      )}
    </div>
  )
}
